import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import AxiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';

export const getDeposits = createAsyncThunk('GET_DEPOSITS', async (data, thunkAPI) => {
  const params = new URLSearchParams({
    page: data.page || 1,
    page_size: data.pageSize || 10,
    text: data.text || '',
    account_id: data.accountId || '',
    solution_type: data.solutionType || 'API',
  });
  const response = await AxiosAdmin.get(`${URL.AGENCY_DEPOSITS}?${params.toString()}`, thunkAPI);
  return response.data;
});

export const recordDeposit = createAsyncThunk('RECORD_DEPOSIT', async (data, thunkAPI) => {
  const response = await AxiosAdmin.post(URL.AGENCY_DEPOSITS, data, thunkAPI);
  return response.data;
});

export const updateDeposit = createAsyncThunk('UPDATE_DEPOSIT', async (data, thunkAPI) => {
  const response = await AxiosAdmin.put(`${URL.AGENCY_DEPOSITS}/${data.id}`, data, thunkAPI);
  return response.data;
});

export const deleteDeposit = createAsyncThunk('DELETE_DEPOSIT', async (data, thunkAPI) => {
  const response = await AxiosAdmin.delete(`${URL.AGENCY_DEPOSITS}/${data.id}`, {}, thunkAPI);
  return response.data;
});

export const getDepositAccounts = createAsyncThunk('GET_DEPOSIT_ACCOUNTS', async (data, thunkAPI) => {
  const solutionType = data?.solutionType || 'API';
  const params = new URLSearchParams({ solutionType });
  if (data?.search) {
    params.set('search', data.search);
  }
  const response = await AxiosAdmin.get(`${URL.CREDIT_LIMIT_ACCOUNTS}?${params.toString()}`, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  isSubmitting: false,
  isAccountsLoading: false,
  depositData: null,
  depositAccounts: [],
};

export const depositSlice = createSlice({
  name: 'deposit',
  initialState,
  reducers: {},
  extraReducers: {
    [getDeposits.pending]: (state) => {
      state.isLoading = true;
    },
    [getDeposits.fulfilled]: (state, action) => {
      state.isLoading = false;
      // Admin may wrap upstream { status, message, data } again → prefer inner list payload
      const level1 = action.payload?.data;
      const level2 = level1?.data;
      if (
        level2 &&
        (Array.isArray(level2.data) ||
          Array.isArray(level2.deposits) ||
          level2.total_count != null ||
          level2.totalCount != null)
      ) {
        state.depositData = level2;
      } else {
        state.depositData = level1;
      }
    },
    [getDeposits.rejected]: (state, action) => {
      state.isLoading = false;
      state.depositData = null;
      toast.error(action?.payload?.message || 'Failed to load deposits');
    },

    [recordDeposit.pending]: (state) => {
      state.isSubmitting = true;
    },
    [recordDeposit.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [recordDeposit.rejected]: (state, action) => {
      state.isSubmitting = false;
      toast.error(action?.payload?.message || 'Failed to record deposit');
    },

    [updateDeposit.pending]: (state) => {
      state.isSubmitting = true;
    },
    [updateDeposit.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [updateDeposit.rejected]: (state, action) => {
      state.isSubmitting = false;
      toast.error(action?.payload?.message || 'Failed to update deposit');
    },

    [deleteDeposit.pending]: (state) => {
      state.isSubmitting = true;
    },
    [deleteDeposit.fulfilled]: (state) => {
      state.isSubmitting = false;
    },
    [deleteDeposit.rejected]: (state, action) => {
      state.isSubmitting = false;
      toast.error(action?.payload?.message || 'Failed to delete deposit');
    },

    [getDepositAccounts.pending]: (state) => {
      state.isAccountsLoading = true;
    },
    [getDepositAccounts.fulfilled]: (state, action) => {
      state.isAccountsLoading = false;
      // Admin wraps reporting response: payload.data.data.accounts (also accept flatter shapes)
      const level1 = action.payload?.data;
      const accounts =
        level1?.data?.accounts ||
        level1?.accounts ||
        action.payload?.accounts ||
        [];
      state.depositAccounts = (Array.isArray(accounts) ? accounts : []).map((acc) => ({
        value: acc.id,
        label: acc.name || acc.account_name || acc.email || acc.id,
        email: acc.email,
        solutionType: acc.solution_type || acc.solutionType,
      }));
    },
    [getDepositAccounts.rejected]: (state, action) => {
      state.isAccountsLoading = false;
      state.depositAccounts = [];
      toast.error(action?.payload?.message || 'Failed to load accounts');
    },
  },
});

export default depositSlice.reducer;
